import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
    const { t } = useTranslation();
    const { user, hasFeatureAccess, isSubscriptionActive, isAdmin } = useAuth();

    const getDaysRemaining = () => {
        if (!user || user.subscription !== 'FREE-TRIAL') return null;
        const expiry = new Date(user.subscriptionExpiry);
        const now = new Date();
        const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
        return daysLeft > 0 ? daysLeft : 0;
    };

    const daysRemaining = getDaysRemaining();
    const subscriptionActive = isSubscriptionActive();

    return (
        <div className="page-transition">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-2">
                        {t('dashboard.page.welcomeUser', { name: user?.firstName })}
                    </h1>
                    <p className="text-gray-600">
                        {t('dashboard.page.workspaceSubtitle')}
                    </p>
                    {user?.organization && (
                        <p className="text-sm text-gray-500 mt-1">
                            {t('dashboard.page.organizationLabel')}: <span className="font-semibold">{user.organization.name}</span>
                            {' • '}
                            {t('dashboard.page.roleLabel')}: <span className="capitalize font-semibold">{user.role}</span>
                        </p>
                    )}
                </div>

                {/* Subscription Status */}
                <div className="mb-8">
                    {user?.subscription === 'FREE-TRIAL' && (
                        <div className={`${subscriptionActive ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded-lg p-4`}>
                            <div className="flex items-start">
                                <div className={`w-10 h-10 ${subscriptionActive ? 'bg-green-100' : 'bg-red-100'} rounded-full flex items-center justify-center mr-4`}>
                                    <svg className={`w-6 h-6 ${subscriptionActive ? 'text-green-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {subscriptionActive ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        )}
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h3 className={`font-semibold ${subscriptionActive ? 'text-green-800' : 'text-red-800'}`}>
                                        {subscriptionActive ? t('dashboard.page.trialActive') : t('dashboard.page.trialExpired')}
                                    </h3>
                                    <p className={`text-sm ${subscriptionActive ? 'text-green-700' : 'text-red-700'}`}>
                                        {subscriptionActive
                                            ? t('dashboard.page.daysRemainingTrial', { count: daysRemaining })
                                            : t('dashboard.page.pleaseUpgrade')}
                                    </p>
                                </div>
                                {subscriptionActive && daysRemaining <= 7 && (
                                    <Link to="/organization-settings" state={{ tab: 'subscription' }} className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors font-semibold min-h-touch min-w-touch">
                                        {t('profile.subscription.upgradeNow')}
                                    </Link>
                                )}
                                {!subscriptionActive && (
                                    <Link to="/organization-settings" state={{ tab: 'subscription' }} className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors font-semibold min-h-touch min-w-touch">
                                        {t('profile.subscription.upgradeNow')}
                                    </Link>
                                )}
                            </div>
                        </div>
                    )}

                    {user?.subscription !== 'FREE-TRIAL' && (
                        subscriptionActive ? (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-center">
                                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-blue-800">
                                            {t('dashboard.page.subscriptionActive', { plan: user?.subscription })}
                                        </h3>
                                        <p className="text-sm text-blue-700">
                                            {t('dashboard.page.thankYouMember')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4">
                                        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-red-800">
                                            {t('dashboard.page.subscriptionExpired', { plan: user?.subscription })}
                                        </h3>
                                        <p className="text-sm text-red-700">
                                            {t('dashboard.page.pleaseRenew')}
                                        </p>
                                    </div>
                                    <Link to="/organization-settings" state={{ tab: 'subscription' }} className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors font-semibold min-h-touch min-w-touch">
                                        {t('profile.subscription.payNow')}
                                    </Link>
                                </div>
                            </div>
                        )
                    )}
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {/* Step 1: Patient Info - Always enabled */}
                    <Link
                        to="/patient-info"
                        className="card hover:shadow-xl transition-shadow group"
                    >
                        <div className="flex items-start">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-blue-200 transition-colors">
                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center mb-2">
                                    <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded mr-2">1</span>
                                    <h3 className="text-xl font-semibold group-hover:text-blue-600 transition-colors">
                                        {t('patientInfo.progress.step1')}
                                    </h3>
                                </div>
                                <p className="text-gray-600">
                                    {t('dashboard.page.step1Desc')}
                                </p>
                            </div>
                            <svg className="w-6 h-6 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </Link>

                    {/* Step 2: Leg Measurements - Disabled until step 1 complete */}
                    <div className="card opacity-60 cursor-not-allowed">
                        <div className="flex items-start">
                            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center mb-2">
                                    <span className="bg-gray-400 text-white text-xs font-bold px-2 py-1 rounded mr-2">2</span>
                                    <h3 className="text-xl font-semibold text-gray-500">
                                        {t('patientInfo.progress.step2')}
                                    </h3>
                                </div>
                                <p className="text-gray-500">
                                    {t('dashboard.page.step2Desc')}
                                </p>
                            </div>
                            <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </div>

                    {/* Step 3: Product Selection - Disabled until previous steps complete */}
                    <div className="card opacity-60 cursor-not-allowed">
                        <div className="flex items-start">
                            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center mb-2">
                                    <span className="bg-gray-400 text-white text-xs font-bold px-2 py-1 rounded mr-2">3</span>
                                    <h3 className="text-xl font-semibold text-gray-500">
                                        {t('patientInfo.progress.step3')}
                                    </h3>
                                </div>
                                <p className="text-gray-500">
                                    {t('dashboard.page.step3Desc')}
                                </p>
                            </div>
                            <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </div>

                    {/* Step 4: Save Order - Disabled until previous steps complete */}
                    <div className="card opacity-60 cursor-not-allowed">
                        <div className="flex items-start">
                            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center mb-2">
                                    <span className="bg-gray-400 text-white text-xs font-bold px-2 py-1 rounded mr-2">4</span>
                                    <h3 className="text-xl font-semibold text-gray-500">
                                        {t('patientInfo.progress.step4')}
                                    </h3>
                                </div>
                                <p className="text-gray-500">
                                    {t('dashboard.page.step4Desc')}
                                </p>
                            </div>
                            <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Features Overview */}
                <div className="card mb-8">
                    <h2 className="text-2xl font-semibold mb-6">{t('dashboard.page.featuresTitle')}</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="font-semibold mb-3 flex items-center">
                                <svg className={`w-5 h-5 mr-2 ${hasFeatureAccess('off-the-shelf') ? 'text-green-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={hasFeatureAccess('off-the-shelf') ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"} />
                                </svg>
                                {t('dashboard.page.offTheShelfTitle')}
                            </h3>
                            <p className="text-sm text-gray-600 ml-7">
                                {t('dashboard.page.offTheShelfDesc')}
                            </p>
                        </div>

                        <div>
                            <h3 className="font-semibold mb-3 flex items-center">
                                <svg className={`w-5 h-5 mr-2 ${hasFeatureAccess('made-to-measure') ? 'text-green-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={hasFeatureAccess('made-to-measure') ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"} />
                                </svg>
                                {t('dashboard.page.madeToMeasureTitle')}
                            </h3>
                            <p className="text-sm text-gray-600 ml-7">
                                {hasFeatureAccess('made-to-measure')
                                    ? t('dashboard.page.madeToMeasureDesc')
                                    : t('dashboard.page.upgradeForMadeToMeasure')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Admin Section */}
                {isAdmin() && (
                    <div className="card mb-8">
                        <h2 className="text-2xl font-semibold mb-6">{t('dashboard.page.adminToolsTitle')}</h2>
                        <div className="grid md:grid-cols-3 gap-4">
                            <Link
                                to="/user-management"
                                className="p-4 border rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                            >
                                <svg className="w-8 h-8 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                                <div>
                                    <h3 className="font-semibold">{t('dashboard.page.userManagementTitle')}</h3>
                                    <p className="text-sm text-gray-600">{t('dashboard.page.userManagementDesc')}</p>
                                </div>
                            </Link>
                            <Link
                                to="/organization-settings"
                                className="p-4 border rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                            >
                                <svg className="w-8 h-8 text-purple-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <div>
                                    <h3 className="font-semibold">{t('dashboard.page.organizationSettingsTitle')}</h3>
                                    <p className="text-sm text-gray-600">{t('dashboard.page.organizationSettingsDesc')}</p>
                                </div>
                            </Link>
                            <Link
                                to="/product-options"
                                className="p-4 border rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                            >
                                <svg className="w-8 h-8 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                </svg>
                                <div>
                                    <h3 className="font-semibold">{t('dashboard.page.productOptionsTitle')}</h3>
                                    <p className="text-sm text-gray-600">{t('dashboard.page.productOptionsDesc')}</p>
                                </div>
                            </Link>
                        </div>
                    </div>
                )}

                {/* Help Section */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
                    <h2 className="text-xl font-semibold mb-4">{t('dashboard.page.needHelpTitle')}</h2>
                    <div className="grid md:grid-cols-3 gap-4">
                        <div>
                            <h3 className="font-semibold mb-2 text-blue-800">{t('dashboard.page.documentationTitle')}</h3>
                            <p className="text-sm text-gray-700">
                                {t('dashboard.page.documentationDesc')}
                            </p>
                        </div>
                        <div>
                            <h3 className="font-semibold mb-2 text-purple-800">{t('dashboard.page.videoTutorialsTitle')}</h3>
                            <p className="text-sm text-gray-700">
                                {t('dashboard.page.videoTutorialsDesc')}
                            </p>
                        </div>
                        <div>
                            <h3 className="font-semibold mb-2 text-green-800">{t('dashboard.page.supportTitle')}</h3>
                            <p className="text-sm text-gray-700">
                                {t('dashboard.page.supportDesc')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
